import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { useGetUser } from "../../../../../components/UserProfileSetting/hooks/userProfileHelper.hooks";
import { label, number } from "../../../../../config";
import { getUserProfileControlsData } from "../../../../../shared/services/auth.service";
import ProfileControlField from "../ProfileControlField";
import { useProfileContext } from "../UserProfile.context";

/**
 * WorkInformationSection component for Work Information section
 * Displays work-related profile controls
 * @author Bhavana
 */
const WorkInformationSection = () => {
  const [profileControlsDataList, setProfileControlDataList] = useState({});
  const [profileControls, setProfileControls] = useState([]);

  const {
    profileData,
    setProfileData,
    setIsEnableSaveButton,
    setUpdatedVariables,
    updatedVariables,
    sortingOrder,
    setSortingOrder,
    initialValues,
    setInitialValues,
  } = useProfileContext();

  const { Manager, JobRole, Department, OfficeLocation } = useSelector((state) => state.auth.user);
  const user = useGetUser();
  const { uId } = useParams();
  const { companyId } = user;
  const { id } = useSelector((state) => state.auth.user);
  const dispatch = useDispatch();

  /**
   * Initializes input values when the component renders.
   * @author Bhavana
   */
  useEffect(() => {
    (async () => {
      if (companyId) {
        const payload = { companyId: companyId, drutasUserId: id };
        const data = await getUserProfileControlsData(payload);
        const { WorkInformations, EmailControls, SortingOrder } = data || {};
        setProfileControls(WorkInformations);
        if (setSortingOrder) {
          setSortingOrder(SortingOrder?.VariableSorting);
        }
      }
    })();
  }, [companyId, uId, id, setSortingOrder]);

  /**
   * Gets the Redux value for special variables based on fixedVariableId
   * @param {number} fixedVariableId - The fixed variable ID
   * @returns {any} The corresponding Redux value
   * @author Bhavana
   */
  const getSpecialVariableReduxValue = useCallback(
    (fixedVariableId) => {
      switch (fixedVariableId) {
        case 7:
          return Manager;
        case 6:
          return JobRole;
        case 3:
          return Department;
        case 8:
          return OfficeLocation;
        default:
          return null;
      }
    },
    [Manager, JobRole, Department, OfficeLocation]
  );

  /**
   * Initializes special variables with Redux values if their values are empty
   * @param {Array} controls - Array of profile controls
   * @returns {Array} Controls with initialized special variables
   * @author Bhavana
   */
  const initializeSpecialVariables = useCallback(
    (controls) => {
      return controls.map((control) => {
        if (control?.isSpecialVariable && (!control.value || control.value === "")) {
          const reduxValue = getSpecialVariableReduxValue(control.fixedVariableId);
          return { ...control, value: reduxValue || control.value };
        }
        return control;
      });
    },
    [getSpecialVariableReduxValue]
  );

  /**
   * Sorts profile controls based on sorting order
   * @param {Array} controls - Array of profile controls
   * @param {string} sortingOrder - Comma-separated sorting order
   * @returns {Array} Sorted profile controls
   * @author Bhavana
   */
  const sortProfileControls = useCallback((controls, sortingOrder) => {
    let sorted = [...controls].filter((control) => !(control?.fixedVariableId >= number.TEN && control?.fixedVariableId <= number.SIXTEEN));
    if (!sortingOrder) return sorted;
    const sortingIds = sortingOrder.split(",").map((id) => id.trim());

    sorted = sorted.sort((a, b) => {
      const aIndex = sortingIds.indexOf(a._id);
      const bIndex = sortingIds.indexOf(b._id);
      if (aIndex !== -1 && bIndex !== -1) {
        return aIndex - bIndex;
      }
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;
      return 0;
    });
    return sorted;
  }, []);

  /**
   * Processes profile controls: sorts them and initializes special variables
   * @author Bhavana
   */
  const sortedProfileControls = useMemo(() => {
    if (!profileControls || !Array.isArray(profileControls)) {
      return [];
    }
    const sorted = sortProfileControls(profileControls, sortingOrder);
    return initializeSpecialVariables(sorted);
  }, [profileControls, sortingOrder, sortProfileControls, initializeSpecialVariables]);

  /**
   * Initialize initial values for work information
   * @author Bhavana
   */
  useEffect(() => {
    if (setInitialValues) {
      setInitialValues((prev) => ({
        ...prev,
        profileData: {
          ...prev.profileData,
          Manager: user.Manager ?? null,
          JobRole: user.JobRole ?? null,
          Department: user.Department ?? null,
          OfficeLocation: user.OfficeLocation ?? null,
        },
      }));
    }
  }, [user.id, user.Manager, user.JobRole, user.Department, user.OfficeLocation, setInitialValues]);

  /**
   * Handles input changes by updating corresponding state variables and the profileData object.
   * Enables save button if value is different from initial, disables if it matches initial.
   * @author Bhavana
   */
  const handleControlValueChange = (event) => {
    let value = event?.value || event?.target?.value;
    const { profileData: initialProfileData } = initialValues || {};

    if (value && typeof value === "object" && value?.hasOwnProperty("value")) {
      value = value?.value;
    }
    let isDifferentFromInitial = false;

    const currentVariable = sortedProfileControls?.find((control) => control._id === event.target.name);

    if (currentVariable?.isSpecialVariable) {
      let initialValue;
      let profileDataKey;

      switch (currentVariable.fixedVariableId) {
        case 7: 
          initialValue = initialProfileData?.Manager;
          profileDataKey = "Manager";
          break;
        case 6: 
          initialValue = initialProfileData?.JobRole;
          profileDataKey = "JobRole";
          break;
        case 3: 
          initialValue = initialProfileData?.Department;
          profileDataKey = "Department";
          break;
        case 8: 
          initialValue = initialProfileData?.OfficeLocation;
          profileDataKey = "OfficeLocation";
          break;
        default:
          initialValue = currentVariable?.value;
          break;
      }

      isDifferentFromInitial = value !== initialValue;

      if (isDifferentFromInitial) {
        setInitialValues((prevState) => ({
          ...prevState,
          profileData: {
            ...prevState.profileData,
            [profileDataKey]: value,
          },
        }));
        if (profileDataKey) {
          setProfileData((prevState) => ({
            ...prevState,
            [profileDataKey]: value,
          }));
        }

        const newVariable = {
          uId: user?.id,
          companyId: companyId,
          variableItemId: event.target.name,
          value: value,
        };
        setUpdatedVariables((prevState) => {
          const updated = [...prevState?.filter((variable) => variable?.variableItemId !== event.target.name), newVariable];
          setIsEnableSaveButton(updated?.length > number.ZERO);
          return updated;
        });
      } else {
        setUpdatedVariables((prevState) => {
          const updated = prevState?.filter((variable) => variable?.variableItemId !== event.target.name);
          setIsEnableSaveButton(updated.length > number.ZERO);
          return updated;
        });
      }
    } else {
      const currentValue = currentVariable?.value;
      isDifferentFromInitial = currentValue !== value;

      if (isDifferentFromInitial) {
        const newVariable = {
          uId: user?.id,
          companyId: companyId,
          variableItemId: event.target.name,
          value: value,
        };
        setUpdatedVariables((prevState) => {
          const updated = [...prevState?.filter((variable) => variable?.variableItemId !== event.target.name), newVariable];
          setIsEnableSaveButton(updated.length > number.ZERO);
          return updated;
        });
      } else {
        setUpdatedVariables((prevState) => {
          const updated = prevState?.filter((variable) => variable?.variableItemId !== event.target.name);
          setIsEnableSaveButton(updated.length > number.ZERO);
          return updated;
        });
      }
    }
  };

  return (
    <div className="profile-section-content bg-light">
        <div className="mt-1">
          <div className="profile-control-fields-container">
            {sortedProfileControls?.map((control) => (
              <ProfileControlField
                key={control?._id}
                control={control}
                profileControlsDataList={profileControlsDataList}
                user={user}
                profileData={profileData}
                handleChange={handleControlValueChange}
                uId={uId}
                initialValues={{ Manager, JobRole, Department, OfficeLocation }}
              />
            ))}
          </div>
        </div>
    </div>
  );
};

export default WorkInformationSection;

